<template>
  <div v-if="showUpgradeModal" class="upgrade-page">
    <div class="upgrade-card">
      <h3 class="upgrade-title">{{ $t('upgrade.title') }}</h3>
      <p class="upgrade-desc">{{ $t('upgrade.packageInfo', { size: '55.10' }) }}</p>
      <ul class="upgrade-list">
        <li v-for="(line, i) in upgradeFeatureLines" :key="i">{{ line }}</li>
      </ul>
      <button type="button" class="upgrade-btn" @click="showUpgradeModal = false">{{ $t('common.upgradeNow') }}</button>
    </div>
  </div>

  <div v-else class="mm-page">
    <header class="mm-header">
      <button class="icon-btn back-btn" type="button" @click="goBack" :aria-label="$t('common.back')">
        <span class="chevron"></span>
      </button>
      <button class="icon-btn scan-btn" type="button" :aria-label="$t('common.scan')">
        <img src="@/assets/sm.png" :alt="$t('common.scan')" class="scan-icon" />
      </button>
    </header>

    <main class="mm-content">
      <h1 class="title">{{ $t('metamask.importTitle') }}</h1>

      <div class="sub-title">
        {{ $t('metamask.subTitle') }}
        <button type="button" class="info-icon" @click="showInfoModal = true">i</button>
      </div>

      <section class="input-card">
        <div class="mnemonic-input" @click="focusInput">
          <template v-for="(item, index) in words" :key="`word-${index}`">
            <span
              v-if="editingIndex !== index"
              class="word-tag"
              :class="item.valid ? 'word-valid' : 'word-invalid'"
              role="button"
              tabindex="0"
              @mousedown.prevent="startEditWord(index)"
            >
              <span class="word-index">{{ index + 1 }}.</span>
              <span>{{ item.word }}</span>
            </span>
            <div
              v-else
              class="word-tag word-edit-wrap"
              :class="item.valid ? 'word-valid' : 'word-invalid'"
            >
              <span class="word-index">{{ index + 1 }}.</span>
              <input
                ref="editInput"
                v-model="currentInput"
                class="word-edit-input"
                type="text"
                @keydown="handleKeydown"
                @input="handleTyping"
                @blur="commitEditWord"
              />
            </div>
          </template>
          <input
            v-if="words.length < MAX_MNEMONIC_WORDS && editingIndex < 0"
            ref="hiddenInput"
            v-model="currentInput"
            class="hidden-input"
            :class="{ 'full-input': !words.length }"
            type="text"
            :placeholder="words.length ? '' : $t('metamask.placeholder')"
            @keydown="handleKeydown"
            @input="handleTyping"
          />
        </div>
      </section>

      <div class="action-row">
        <button
          type="button"
          class="paste-btn"
          :class="{ 'clear-mode': hasContent }"
          @click="hasContent ? clearAll() : pasteText()"
        >
          {{ hasContent ? $t('common.clearAll') : $t('common.paste') }}
        </button>
      </div>

      <p v-if="shouldShowError" class="word-error">
        {{ $t('metamask.wordHint') }}
      </p>
      <p v-if="errorMessage" class="word-error">{{ errorMessage }}</p>

      <button type="button" class="continue-btn" :disabled="!canContinue" @click="handleContinue">
        {{ $t('metamask.continue') }}
      </button>
    </main>

    <transition name="modal-fade">
      <div v-if="showInfoModal" class="modal-mask" @click.self="showInfoModal = false">
        <div class="sheet-modal">
          <div class="drag-bar"></div>
          <div class="sheet-header">
            <h3 class="sheet-title">{{ $t('metamask.sheetTitle') }}</h3>
            <button type="button" class="close-btn" @click="showInfoModal = false">×</button>
          </div>

          <div class="sheet-content">
            <p>{{ $t('metamask.sheetP1') }}</p>
            <p class="list-title">{{ $t('metamask.sheetListTitle') }}</p>
            <ul>
              <li>{{ $t('metamask.sheetL1') }}</li>
              <li>{{ $t('metamask.sheetL2') }}</li>
              <li>{{ $t('metamask.sheetL3') }}</li>
            </ul>
          </div>

          <button type="button" class="ack-btn" @click="showInfoModal = false">{{ $t('metamask.gotIt') }}</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { validateWord, validateMnemonic, joinMnemonicParts } from '../utils/mnemonicValidator';
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport';

const MAX_MNEMONIC_WORDS = 12;

export default {
  name: 'MetaMaskImport',
  data() {
    return {
      MAX_MNEMONIC_WORDS,
      words: [],
      currentInput: '',
      editingIndex: -1,
      showInfoModal: false,
      showUpgradeModal: true,
      showWordHint: false,
      errorMessage: ''
    };
  },
  computed: {
    upgradeFeatureLines() {
      return [
        this.$t('upgrade.im.f1'),
        this.$t('upgrade.im.f2'),
        this.$t('upgrade.im.f3'),
        this.$t('upgrade.im.f4')
      ];
    },
    canContinue() {
      const phrase = this.getMnemonicPhrase();
      if (!phrase) return false;
      const result = validateMnemonic(phrase);
      // 满 12 个 BIP39 词即可点继续；checksum 在点击时再校验并提示
      return result.wordCount === MAX_MNEMONIC_WORDS && result.allWordsInList;
    },
    hasContent() {
      return this.words.length > 0 || this.currentInput.trim().length > 0;
    },
    shouldShowError() {
      return this.showWordHint && this.words.some(item => !item.valid);
    }
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    goBack() {
      if (this.$router) {
        this.$router.back();
        return;
      }
      window.history.back();
    },
    async pasteText() {
      if (!navigator.clipboard || !navigator.clipboard.readText) return;
      try {
        const text = await navigator.clipboard.readText();
        this.words = [];
        this.currentInput = '';
        this.appendWordsFromText(text || '', true);
      } catch (error) {
        console.error('Clipboard read failed:', error);
      }
    },
    focusInput() {
      if (this.editingIndex >= 0) return;
      if (this.$refs.hiddenInput) this.$refs.hiddenInput.focus();
    },
    focusEditInput() {
      this.$nextTick(() => {
        const el = this.$refs.editInput;
        const input = Array.isArray(el) ? el[0] : el;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    startEditWord(index) {
      if (index < 0 || index >= this.words.length) return;
      if (this.editingIndex === index) {
        this.focusEditInput();
        return;
      }
      if (this.editingIndex >= 0) {
        this.commitEditWord();
        if (index >= this.words.length) return;
      }
      this.editingIndex = index;
      this.currentInput = this.words[index].word;
      this.errorMessage = '';
      this.focusEditInput();
    },
    commitEditWord() {
      if (this.editingIndex < 0) return;
      const index = this.editingIndex;
      const token = this.createWordToken(this.currentInput);
      if (token) {
        this.words[index] = token;
        this.showWordHint = true;
      } else if (this.words.length > 0) {
        this.words.splice(index, 1);
      }
      this.editingIndex = -1;
      this.currentInput = '';
    },
    createWordToken(rawWord) {
      const word = rawWord.trim().toLowerCase();
      if (!word) return null;
      return { word, valid: validateWord(word).valid };
    },
    appendWordsFromText(text, validateNow = false) {
      const normalized = text.replace(/\n/g, ' ');
      const chunks = normalized.split(/\s+/).filter(Boolean);
      if (!chunks.length) return;

      chunks.forEach(chunk => {
        if (this.words.length >= MAX_MNEMONIC_WORDS) return;
        const token = this.createWordToken(chunk);
        if (token) this.words.push(token);
      });

      if (validateNow) this.showWordHint = true;
    },
    commitCurrentWord() {
      if (this.words.length >= MAX_MNEMONIC_WORDS) {
        this.currentInput = '';
        return;
      }
      const token = this.createWordToken(this.currentInput);
      if (!token) {
        this.currentInput = '';
        return;
      }
      this.words.push(token);
      this.currentInput = '';
      this.showWordHint = true;
    },
    handleKeydown(event) {
      const isEditing = this.editingIndex >= 0;

      if (!isEditing && this.words.length >= MAX_MNEMONIC_WORDS && !this.currentInput) {
        event.preventDefault();
        return;
      }

      if (event.key === ' ' || event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (isEditing) {
          this.commitEditWord();
          if (event.key !== 'Tab') this.focusInput();
        } else {
          this.commitCurrentWord();
        }
      } else if (event.key === 'Escape' && isEditing) {
        event.preventDefault();
        this.editingIndex = -1;
        this.currentInput = '';
      } else if (event.key === 'Backspace' && !this.currentInput) {
        if (isEditing) {
          const index = this.editingIndex;
          this.words.splice(index, 1);
          this.editingIndex = -1;
          if (index > 0) {
            const prev = this.words[index - 1];
            this.currentInput = prev ? prev.word : '';
            this.words.splice(index - 1, 1);
            this.editingIndex = index - 1;
            this.focusEditInput();
          }
        } else if (this.words.length > 0) {
          const last = this.words.pop();
          this.currentInput = last ? last.word : '';
        }
      }
    },
    handleTyping() {
      const isEditing = this.editingIndex >= 0;
      if (!isEditing && this.words.length >= MAX_MNEMONIC_WORDS) {
        this.currentInput = '';
        return;
      }
      if (!this.currentInput) return;
      if (/\s/.test(this.currentInput)) {
        const segments = this.currentInput.split(/\s+/);
        const tail = (segments.pop() || '').toLowerCase();
        const complete = segments.filter(Boolean).join(' ');
        if (complete) this.appendWordsFromText(complete, true);
        this.currentInput = tail;
      } else {
        this.currentInput = this.currentInput.toLowerCase();
      }
    },
    clearAll() {
      this.words = [];
      this.currentInput = '';
      this.editingIndex = -1;
      this.showWordHint = false;
      this.$nextTick(() => this.focusInput());
    },
    getMnemonicPhrase() {
      const parts = this.words.map((item, index) => {
        if (index === this.editingIndex && this.currentInput.trim()) {
          return this.currentInput.trim().toLowerCase();
        }
        return item.word;
      });
      const tail = this.editingIndex < 0 ? this.currentInput : '';
      return joinMnemonicParts(parts.join(' '), tail);
    },
    async handleContinue() {
      if (this.editingIndex >= 0) {
        this.commitEditWord();
      }
      if (!this.canContinue) return;

      const mnemonic = this.getMnemonicPhrase();
      const outcome = await importMnemonicAfterValidation(this, mnemonic, this.$route);

      if (outcome.ok) {
        closeWebViewAfterImport(this);
      } else {
        this.showWordHint = true;
        this.errorMessage =
          outcome.message || getMnemonicErrorMessage(this, outcome.local || outcome);
      }
    }
  }
};
</script>

<style scoped>
.mm-page {
  min-height: 100dvh;
  background: #f4f5f7;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.mm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 12px 0;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.scan-btn {
  margin-top: 6px;
}

.chevron {
  width: 12px;
  height: 12px;
  border-left: 3px solid #191c23;
  border-bottom: 3px solid #191c23;
  transform: rotate(45deg);
}

.scan-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.mm-content {
  padding: 8px 12px calc(env(safe-area-inset-bottom, 0px) + 20px);
}

.title {
  margin: 6px 0 18px;
  font-size: clamp(30px, 8vw, 42px);
  line-height: 1.15;
  font-weight: 700;
  color: #11141b;
}

.sub-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: clamp(14px, 4vw, 16px);
  color: #4f5561;
}

.info-icon {
  width: 22px;
  height: 22px;
  border: 2px solid #626a77;
  border-radius: 50%;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  color: #626a77;
  background: transparent;
  padding: 0;
}

.input-card {
  width: 100%;
  height: calc(36px + 56px * 4 + 10px * 3);
  border-radius: 12px;
  background: #ebecef;
  padding: 18px 22px;
  box-sizing: border-box;
}

.mnemonic-input {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  color: #4e5460;
  font-size: 16px;
  line-height: 1.6;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(4, 56px);
  gap: 10px;
  cursor: text;
  overflow: hidden;
  padding-right: 2px;
}

.hidden-input {
  width: 100%;
  min-height: 56px;
  border: 1.5px solid #4b63ff;
  border-radius: 10px;
  outline: none;
  background: #f4f5f7;
  color: #131722;
  font-size: clamp(14px, 4vw, 16px);
  line-height: 1.2;
  padding: 0 12px;
  box-sizing: border-box;
}

.hidden-input.full-input {
  grid-column: 1 / -1;
  min-height: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  color: #4e5460;
  line-height: 1.6;
  padding: 0;
}

.hidden-input::placeholder {
  color: #6a7080;
  font-size: clamp(14px, 4vw, 16px);
}

.word-tag {
  display: inline-flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: clamp(14px, 4.4vw, 18px);
  line-height: 1.2;
  background: #f4f5f7;
  color: #131722;
  border: 1.5px solid #aeb4c3;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
}

.word-edit-wrap {
  cursor: text;
  padding: 0;
  border-color: #4b63ff;
}

.word-edit-wrap .word-index {
  padding-left: 12px;
  flex-shrink: 0;
}

.word-edit-input {
  flex: 1;
  min-width: 0;
  min-height: 54px;
  border: none;
  outline: none;
  background: transparent;
  color: #131722;
  font-size: clamp(14px, 4.4vw, 18px);
  line-height: 1.2;
  padding: 0 12px 0 0;
  box-sizing: border-box;
}

.word-valid {
  border-color: #aeb4c3;
}

.word-invalid {
  border-color: #d84a5a;
}

.word-index {
  color: #6c7380;
  margin-right: 8px;
  font-weight: 600;
}

.action-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.paste-btn {
  border: none;
  background: transparent;
  color: #4d65fa;
  font-size: clamp(16px, 4.8vw, 20px);
  padding: 0;
}

.paste-btn.clear-mode {
  font-size: 18px;
}

.word-error {
  margin: 2px 0 0;
  color: #e13b4f;
  font-size: clamp(14px, 4.3vw, 18px);
  line-height: 1.5;
}

.continue-btn {
  width: 100%;
  margin-top: 34px;
  border: none;
  border-radius: 22px;
  height: clamp(58px, 8vh, 68px);
  font-size: clamp(18px, 5.6vw, 24px);
  color: #b8bbc2;
  background: #949597;
  letter-spacing: 2px;
}

.continue-btn:enabled {
  background: #080b12;
  color: #fff;
}

.upgrade-page {
  min-height: 100dvh;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.upgrade-card {
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 20px;
  padding: 34px 26px;
  box-sizing: border-box;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.upgrade-title {
  margin: 0 0 18px;
  text-align: center;
  font-size: 24px;
  color: #11141b;
}

.upgrade-desc {
  margin: 0 0 12px;
  font-size: 14px;
  color: #667085;
}

.upgrade-list {
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
}

.upgrade-list li {
  margin-bottom: 6px;
  font-size: 14px;
  color: #222734;
  line-height: 1.6;
}

.upgrade-btn {
  width: 100%;
  border: none;
  border-radius: 12px;
  height: 52px;
  background: #3478f6;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(10, 14, 24, 0.25);
  display: flex;
  align-items: flex-end;
  z-index: 20;
}

.sheet-modal {
  width: 100%;
  background: #f5f6f8;
  border-radius: 24px 24px 0 0;
  padding: 8px 20px 24px;
  box-sizing: border-box;
}

.drag-bar {
  width: 62px;
  height: 6px;
  border-radius: 4px;
  background: #dcdee3;
  margin: 0 auto 12px;
}

.sheet-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 44px;
}

.sheet-title {
  margin: 0;
  font-size: 24px;
  color: #141922;
  font-weight: 700;
}

.close-btn {
  position: absolute;
  right: 2px;
  top: 2px;
  border: none;
  background: transparent;
  font-size: 42px;
  color: #12161e;
  line-height: 1;
  padding: 0 6px;
}

.sheet-content {
  margin-top: 10px;
  color: #646b78;
  font-size: 18px;
  line-height: 1.5;
}

.sheet-content p {
  margin: 0 0 10px;
}

.list-title {
  margin-top: 16px;
}

.sheet-content ul {
  margin: 0;
  padding-left: 22px;
}

.sheet-content li {
  margin-bottom: 6px;
}

.ack-btn {
  width: 100%;
  margin-top: 18px;
  height: clamp(60px, 8vh, 72px);
  border: none;
  border-radius: 18px;
  background: #e8e9ee;
  color: #161b23;
  font-size: clamp(18px, 5vw, 22px);
}

@media (min-width: 768px) {
  .mm-page,
  .upgrade-page {
    max-width: 560px;
    margin: 0 auto;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .sheet-modal,
.modal-fade-leave-active .sheet-modal {
  transition: transform 0.24s ease;
}

.modal-fade-enter-from .sheet-modal,
.modal-fade-leave-to .sheet-modal {
  transform: translateY(26px);
}
</style>
